import dayjs, { type Dayjs } from 'dayjs';
import type {
  ProjectStatEntry,
  SourceStatEntry,
  TimelineEntry,
  UploadStatsGranularity,
} from '@/api/uploadRecords';
import { getSourceLabels, SOURCE_HEX_COLORS } from '@/constants/upload';
import type { Translate } from '@/i18n/translate';

export type Period = 'today' | 'week' | 'month' | 'last_week' | 'last_month' | 'custom';

export const PERIOD_LABELS: Record<Period, string> = {
  today: '今日',
  week: '本周',
  month: '本月',
  last_week: '上周',
  last_month: '上个月',
  custom: '自定义',
};

export const getPeriodOptions = (t: Translate) =>
  (Object.keys(PERIOD_LABELS) as Period[]).map((value) => ({
    value,
    label: t(PERIOD_LABELS[value]),
  }));

export const STATUS_COLORS = {
  done: '#52c41a',
  failed: '#d9776f',
  uploading: '#fa8c16',
};

/** 周一为一周起点（不依赖 dayjs locale）。 */
export const startOfWeek = (day: Dayjs): Dayjs =>
  day.subtract((day.day() + 6) % 7, 'day').startOf('day');

export const periodToRange = (
  period: Period,
  customRange: [Dayjs, Dayjs] | null,
): [Dayjs, Dayjs] | null => {
  const today = dayjs().startOf('day');
  if (period === 'today') return [today, today];
  if (period === 'week') return [startOfWeek(today), today];
  if (period === 'month') return [today.startOf('month'), today];
  if (period === 'last_week') {
    const start = startOfWeek(today).subtract(7, 'day');
    return [start, start.add(6, 'day')];
  }
  if (period === 'last_month') {
    const start = today.subtract(1, 'month').startOf('month');
    return [start, start.endOf('month').startOf('day')];
  }
  return customRange;
};

export const deriveGranularity = (from: Dayjs, to: Dayjs): UploadStatsGranularity => {
  const span = to.startOf('day').diff(from.startOf('day'), 'day');
  if (span <= 31) return 'day';
  if (span <= 180) return 'week';
  return 'month';
};

export const buildTimelineOption = (timeline: TimelineEntry[], t: Translate) => ({
  tooltip: { trigger: 'axis' as const, axisPointer: { type: 'shadow' as const } },
  legend: { data: [t('成功'), t('失败'), t('上传中')], bottom: 0 },
  grid: { left: 48, right: 20, top: 20, bottom: 48 },
  xAxis: {
    type: 'category' as const,
    data: timeline.map((item) => item.period),
    axisLabel: { rotate: timeline.length > 10 ? 45 : 0, fontSize: 11 },
  },
  yAxis: { type: 'value' as const, name: t('上传次数'), minInterval: 1 },
  series: [
    {
      name: t('成功'),
      type: 'bar' as const,
      stack: 'total',
      data: timeline.map((item) => item.done),
      itemStyle: { color: STATUS_COLORS.done },
      emphasis: { focus: 'series' as const },
    },
    {
      name: t('失败'),
      type: 'bar' as const,
      stack: 'total',
      data: timeline.map((item) => item.failed),
      itemStyle: { color: STATUS_COLORS.failed },
      emphasis: { focus: 'series' as const },
    },
    {
      name: t('上传中'),
      type: 'bar' as const,
      stack: 'total',
      data: timeline.map((item) => item.uploading),
      itemStyle: { color: STATUS_COLORS.uploading },
      emphasis: { focus: 'series' as const },
    },
  ],
});

export const buildSourceOption = (bySource: SourceStatEntry[], t: Translate) => {
  const sourceLabels = getSourceLabels(t);
  return {
    tooltip: { trigger: 'item' as const, formatter: '{b}: {c} ({d}%)' },
    legend: { orient: 'horizontal' as const, bottom: 0 },
    series: [
      {
        name: t('来源'),
        type: 'pie' as const,
        radius: ['40%', '68%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
        label: { show: true, formatter: '{b}: {c}' },
        data: bySource
          .filter((item) => item.total > 0)
          .map((item) => ({
            value: item.total,
            name: sourceLabels[item.source]?.label ?? item.source,
            itemStyle: { color: SOURCE_HEX_COLORS[item.source] },
          })),
      },
    ],
  };
};

export const buildProjectOption = (byProject: ProjectStatEntry[], t: Translate) => {
  const top = [...byProject].sort((a, b) => b.total - a.total).slice(0, 15);
  return {
    tooltip: { trigger: 'axis' as const, axisPointer: { type: 'shadow' as const } },
    legend: { data: [t('成功'), t('失败'), t('上传中')], bottom: 0 },
    grid: { left: 140, right: 30, top: 10, bottom: 32 },
    xAxis: { type: 'value' as const, minInterval: 1 },
    yAxis: {
      type: 'category' as const,
      data: top.map((item) => item.project_name || t('项目 {id}', { id: item.project_id })),
      inverse: true,
      axisLabel: { fontSize: 11 },
    },
    series: [
      {
        name: t('成功'),
        type: 'bar' as const,
        stack: 'total',
        data: top.map((item) => item.done),
        itemStyle: { color: STATUS_COLORS.done },
      },
      {
        name: t('失败'),
        type: 'bar' as const,
        stack: 'total',
        data: top.map((item) => item.failed),
        itemStyle: { color: STATUS_COLORS.failed },
      },
      {
        name: t('上传中'),
        type: 'bar' as const,
        stack: 'total',
        data: top.map((item) => item.uploading),
        itemStyle: { color: STATUS_COLORS.uploading },
      },
    ],
  };
};
